/**
 * PipelineErrorsTable — carts in `pipeline-error` state.
 *
 * The v3 backend doesn't expose a dedicated "carts in pipeline-error"
 * endpoint yet; this surface is wired against `superAdmin.records.browse`
 * (which returns post-resolution rows) as a stop-gap and degrades to an
 * informational empty state when the data isn't available.
 *
 * Once Wave 8.1 backend ships `/v3/incidents/pipeline-errors`, swap the
 * fetch in `loadRows` and the table renders the same rows without code
 * changes elsewhere.
 */

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, humanAgo, rowsToCsv } from '@/utils/time';

interface IncidentRow {
  cartId: string;
  branch?: string;
  customerName?: string;
  failedStep?: string;
  failedAt?: string;
  errorMessage?: string;
}

export function PipelineErrorsTable(): JSX.Element {
  const api = useApiClient();
  const [rows, setRows] = useState<IncidentRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Stop-gap fetch: when /v3/incidents/pipeline-errors lands, replace.
      // For now we surface the records-browse output filtered to carts.
      const resp = await api.retail.superAdmin.records.browse({
        entityType: 'Cart',
        top: 100,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      const mapped: IncidentRow[] = data.map((r) => ({
        cartId: r.entityKey,
        branch: r.branch,
        customerName: r.customerName ?? undefined,
        failedStep: undefined,
        failedAt: r.resolvedAt,
        errorMessage: r.resolutionCode,
      }));
      setRows(mapped);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Until /v3/incidents/pipeline-errors ships, the records-browse stop-gap
      // 404s. Surface a friendly "coming soon" rather than a scary error.
      const friendly = msg.includes('404') || msg.toLowerCase().includes('not found')
        ? 'Pipeline errors view — backend route pending. Check back after the next deploy.'
        : msg;
      setError(friendly);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const exportCsv = (): void => {
    if (!rows || rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(`pipeline-errors-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Pipeline errors</h3>
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void loadRows()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={!rows || rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows && rows.length === 0 && (
        <div className="empty">
          No pipeline-error rows. (Note: a dedicated /v3/incidents/pipeline-errors
          backend route is pending — counts may under-report until it lands.)
        </div>
      )}

      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Cart</th>
              <th>Branch</th>
              <th>Customer</th>
              <th>Failed step</th>
              <th>Age</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cartId}>
                <td>
                  <code>{r.cartId.slice(0, 8)}…</code>
                </td>
                <td>{r.branch ?? '—'}</td>
                <td>{r.customerName ?? '—'}</td>
                <td>{r.failedStep ?? '—'}</td>
                <td>
                  {r.failedAt
                    ? `${humanAgo(r.failedAt)} (${formatLocal(r.failedAt)})`
                    : '—'}
                </td>
                <td>{r.errorMessage ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
